import { DevToolsMicroLabel } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsMicroLabel';
import {
  CARD_TITLE,
  SURFACE,
  TONE,
} from 'Modules/softphone/components/SoftphoneDevTools/styles';
import { selectPermissions, useSoftphoneStore } from 'Modules/softphone/store';
import React from 'react';
import { useShallow } from 'zustand/react/shallow';
import { cn } from '~/lib/utils';

/**
 * The store's flag names read as code. Dropping `can` gets most of them to the
 * control they gate; these two carry a verb as well, and the rows must name the
 * control the user sees — the same word the "Views open" chips below use, or
 * the block contradicts itself about the same button.
 */
const PERMISSION_LABELS: Record<string, string> = {
  canUseKeypad: 'Keypad',
  canUseVideo: 'Video',
};

/**
 * CallsControlsStatus - Which call-control views are open, and what is permitted
 *
 * The permission flags are derived state (`selectPermissions`), so this is
 * the only place they can be read back and checked against what the UI
 * offers.
 */
export const CallsControlsStatus: React.FC = () => {
  const {
    isKeypadActive,
    isVideoActive,
    isTransferActive,
    isMergeActive,
    ...permissions
  } = useSoftphoneStore(
    useShallow((state) => ({
      isKeypadActive: state.isKeypadActive,
      isVideoActive: state.isVideoActive,
      isTransferActive: state.isTransferActive,
      isMergeActive: state.isMergeActive,
      // Flattened so the shallow compare sees the individual booleans;
      // `selectPermissions` builds a new object on every call.
      ...selectPermissions(state),
    }))
  );

  const openViews = [
    { label: 'Keypad', active: isKeypadActive },
    { label: 'Video', active: isVideoActive },
    { label: 'Transfer', active: isTransferActive },
    { label: 'Merge', active: isMergeActive },
  ].filter(({ active }) => active);

  return (
    <div className={SURFACE.card}>
      <div className={cn(SURFACE.cardHeader, CARD_TITLE)}>
        Call controls &amp; permissions
      </div>

      <div className="grid grid-cols-2 gap-1.5 p-3">
        {Object.entries(permissions).map(([key, allowed]) => {
          const tone = allowed ? TONE.ok : TONE.muted;

          return (
            <div
              key={key}
              className={cn(
                'flex items-center gap-2 px-[9px] py-[7px]',
                tone.inset
              )}
            >
              <span
                className={cn(
                  'h-1.5 w-1.5 flex-shrink-0 rounded-full',
                  tone.dot
                )}
              />
              <span className="min-w-0 flex-1 truncate font-sp-mono text-[11.5px] text-zinc-200">
                {PERMISSION_LABELS[key] ?? key.replace('can', '')}
              </span>
              <span
                className={cn(
                  'text-[9.5px] font-semibold uppercase tracking-[0.06em]',
                  tone.text
                )}
              >
                {allowed ? 'allowed' : 'blocked'}
              </span>
            </div>
          );
        })}
      </div>

      {openViews.length > 0 && (
        <div className="border-t border-zinc-800 px-3 py-2.5">
          <DevToolsMicroLabel className="mb-1.5">Views open</DevToolsMicroLabel>
          <div className="flex flex-wrap gap-1.5">
            {openViews.map(({ label }) => (
              <span
                key={label}
                className="rounded border border-emerald-800 bg-emerald-950 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-400"
              >
                {label}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
